/**
 * 错误处理拦截器
 *
 * 错误码与后端保持一致（参考 v2）：
 * - 0: 成功
 * - 401/403: Token 失效，需要重新登录
 * - 500: 服务器错误
 */

import { CsdError } from '../../../types/error'
import { ERROR_CODES, ERROR_MESSAGES } from '../../../constants/errors'

type LoginRequiredCallbackResult = boolean | void | Promise<boolean | void>

// 登录事件回调
let onLoginRequired: ((failedToken?: string) => LoginRequiredCallbackResult) | null = null

// 标记是否已经触发过登录事件（防止重复触发）
let hasTriggeredLoginRequired = false

/**
 * 设置 storage 前缀
 */
export function setStoragePrefix(prefix: string) {
  void prefix
}

/**
 * 设置登录事件回调
 */
export function setLoginRequiredCallback(
  callback: ((failedToken?: string) => LoginRequiredCallbackResult) | null
) {
  onLoginRequired = callback
}

/**
 * 重置登录事件标记（登录成功后调用）
 */
export function resetLoginRequiredFlag() {
  hasTriggeredLoginRequired = false
}

function getResponseFromError(error: any) {
  if (error instanceof CsdError) {
    return error.response as any
  }
  return error?.response
}

function getRequestTokenFromResponse(response: any): string | undefined {
  const headers = response?.config?.headers || {}
  const token = headers.Authorization || headers.authorization
  return typeof token === 'string' && token.trim() ? token.trim() : undefined
}

async function notifyLoginRequired(failedToken?: string) {
  if (hasTriggeredLoginRequired) return
  hasTriggeredLoginRequired = true

  // 只通知项目侧统一处理，不在 SDK 内部先清 token，避免旧请求迟到误删新登录态。
  // 业务层返回 false 表示这是旧请求迟到，不能占用后续登录失效提示。
  const isHandled = await onLoginRequired?.(failedToken)
  if (isHandled === false) {
    hasTriggeredLoginRequired = false
  }
}

/**
 * 创建错误处理拦截器
 */
export function createErrorInterceptor() {
  return async (error: any): Promise<any> => {
    // 处理响应错误
    const response = getResponseFromError(error)
    if (response) {
      const { status, data } = response

      // 根据状态码映射错误
      let errorCode: number = ERROR_CODES.UNKNOWN_ERROR
      let errorMessage = ERROR_MESSAGES[errorCode]

      // 401/403 错误：Token 失效，需要重新登录
      if (status === 401 || status === 403 || data?.code === 403) {
        errorCode = status === 401 ? ERROR_CODES.AUTH_FAILED : ERROR_CODES.TOKEN_INVALID
        errorMessage = ERROR_MESSAGES[errorCode]

        await notifyLoginRequired(getRequestTokenFromResponse(response))
      } else if (status >= 500) {
        errorCode = ERROR_CODES.SERVER_ERROR
        errorMessage = ERROR_MESSAGES[errorCode]
      }

      // 如果服务端返回了错误信息，使用服务端的
      if (data?.message || data?.msg) {
        errorMessage = data.message || data.msg
      }

      if (data?.code) {
        errorCode = data.code
      }

      return Promise.reject(new CsdError(errorMessage, errorCode, response))
    }

    // 处理请求错误（网络错误、超时等）
    return Promise.reject(
      new CsdError(
        error.message || ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR],
        ERROR_CODES.UNKNOWN_ERROR
      )
    )
  }
}
